/**
 * i18n Database Service
 * Helper functions for working with multi-language database fields
 */

const LANGUAGES = ['nl', 'fr', 'de', 'en'];

export const i18nService = {
  /**
   * Get translated field value based on current language
   * @param {Object} data - Database object containing language-suffixed fields
   * @param {string} fieldName - Base field name (without language suffix)
   * @param {string} language - Language code (nl, fr, de, en)
   * @param {string} fallbackLang - Fallback language if translation missing (default: 'en')
   * @returns {string} Translated value or fallback
   */
  getTranslatedField(data, fieldName, language, fallbackLang = 'en') {
    if (!data) return '';

    // Try requested language
    const langField = `${fieldName}_${language}`;
    if (data[langField]) return data[langField];

    // Try fallback language
    const fallbackField = `${fieldName}_${fallbackLang}`;
    if (data[fallbackField]) return data[fallbackField];

    // Try any available language
    for (const lang of LANGUAGES) {
      const field = `${fieldName}_${lang}`;
      if (data[field]) return data[field];
    }

    // Return empty string if no translation found
    return '';
  },

  /**
   * Extract multi-language values from database object
   * @param {Object} data - Database object
   * @param {string} fieldName - Base field name
   * @returns {Object} Object with language keys: { nl: '', fr: '', de: '', en: '' }
   */
  extractMultiLangField(data, fieldName) {
    const result = {};

    LANGUAGES.forEach(lang => {
      const langField = `${fieldName}_${lang}`;
      result[lang] = data?.[langField] || '';
    });

    return result;
  },

  /**
   * Convert multi-language object to database-ready format
   * @param {string} fieldName - Base field name
   * @param {Object} multiLangValue - Object with language keys: { nl: '', fr: '', de: '', en: '' }
   * @returns {Object} Object with language-suffixed fields
   */
  setTranslatedFields(fieldName, multiLangValue) {
    const result = {};

    LANGUAGES.forEach(lang => {
      const langField = `${fieldName}_${lang}`;
      result[langField] = multiLangValue[lang] || '';
    });

    return result;
  },

  /**
   * Check if a multi-language field has any content
   * @param {Object} multiLangValue - Object with language keys
   * @returns {boolean} True if any language has content
   */
  hasContent(multiLangValue) {
    return LANGUAGES.some(lang => multiLangValue?.[lang]?.trim());
  },

  /**
   * Get the first non-empty translation
   * @param {Object} multiLangValue - Object with language keys
   * @param {string} preferredLang - Preferred language to try first
   * @returns {string} First non-empty translation found
   */
  getFirstAvailable(multiLangValue, preferredLang = 'nl') {
    if (multiLangValue?.[preferredLang]?.trim()) {
      return multiLangValue[preferredLang];
    }

    for (const lang of LANGUAGES) {
      if (multiLangValue?.[lang]?.trim()) {
        return multiLangValue[lang];
      }
    }

    return '';
  },

  /**
   * Validate that required languages have content
   * @param {Object} multiLangValue - Object with language keys
   * @param {Array} requiredLangs - Array of required language codes
   * @returns {Object} Validation result: { valid: boolean, missing: [] }
   */
  validateRequired(multiLangValue, requiredLangs = ['nl', 'en']) {
    const missing = [];

    requiredLangs.forEach(lang => {
      if (!multiLangValue?.[lang]?.trim()) {
        missing.push(lang);
      }
    });

    return {
      valid: missing.length === 0,
      missing
    };
  },

  /**
   * Copy value from one language to all others
   * @param {Object} multiLangValue - Object with language keys
   * @param {string} sourceLang - Source language to copy from
   * @returns {Object} Updated multi-language object
   */
  copyToAll(multiLangValue, sourceLang = 'en') {
    const sourceValue = multiLangValue?.[sourceLang] || '';
    const result = {};

    LANGUAGES.forEach(lang => {
      result[lang] = sourceValue;
    });

    return result;
  },

  /**
   * Initialize empty multi-language object
   * @returns {Object} Empty object with all language keys
   */
  createEmpty() {
    const result = {};
    LANGUAGES.forEach(lang => {
      result[lang] = '';
    });
    return result;
  },

  /**
   * Get supported languages
   * @returns {Array} Array of language codes
   */
  getSupportedLanguages() {
    return [...LANGUAGES];
  }
};

export default i18nService;
